/**
 * Pure scoring. No I/O, no clock, no randomness — the server calls these and is
 * authoritative, but they are deterministic so they can be unit-tested and, if
 * ever useful, re-run on the client for animation purposes.
 *
 * SPEC §2.4:
 *   points per correct pick, in round r = 100 + 50 × (r − 1)
 *   perfect read (both top AND bottom correct) multiplies that turn's total ×1.5
 *   the turn player scores nothing on their own turn
 */
import { PERFECT_READ_MULTIPLIER, POINTS_BASE, POINTS_ROUND_STEP } from './constants.js';
import type {
  Attribution,
  Leaderboard,
  LeaderboardRow,
  PlayerId,
  Prediction,
  Ranking,
  ScoreDelta,
  Superlative,
  TurnRecord,
} from './state.js';

/* -------------------------------------------------------------------------- */
/* Per-pick value                                                              */
/* -------------------------------------------------------------------------- */

/** 100, 150, 200, 250 … round 10 = 550. */
export function pointsPerCorrectPick(round: number): number {
  if (!Number.isInteger(round) || round < 1) {
    throw new RangeError(`round must be an integer >= 1, got ${round}`);
  }
  return POINTS_BASE + POINTS_ROUND_STEP * (round - 1);
}

/* -------------------------------------------------------------------------- */
/* One predictor, one turn                                                     */
/* -------------------------------------------------------------------------- */

export interface RankingEnds {
  top: PlayerId | null;
  bottom: PlayerId | null;
}

/** The only two slots with money on them (SPEC §2.3 phase 4). */
export function rankingEnds(ranking: Ranking): RankingEnds {
  if (ranking.length === 0) return { top: null, bottom: null };
  const top = ranking[0] ?? null;
  const bottom = ranking.length > 1 ? (ranking[ranking.length - 1] ?? null) : null;
  return { top, bottom };
}

export interface PredictionScore {
  correctTop: boolean;
  correctBottom: boolean;
  perfectRead: boolean;
  points: number;
}

const NOTHING: PredictionScore = {
  correctTop: false,
  correctBottom: false,
  perfectRead: false,
  points: 0,
};

/**
 * Score a single predictor's picks against the canonical ranking.
 *
 * `prediction` is null when the player did not submit before the guillotine —
 * they score zero. Partial picks cannot reach here: the wire schema requires
 * both `top` and `bottom`, which is how "partial picks earn nothing" is enforced.
 */
export function scorePrediction(
  round: number,
  prediction: Prediction | null | undefined,
  ranking: Ranking,
): PredictionScore {
  if (!prediction) return { ...NOTHING };

  const { top, bottom } = rankingEnds(ranking);
  const correctTop = top !== null && prediction.top === top;
  const correctBottom = bottom !== null && prediction.bottom === bottom;

  const perPick = pointsPerCorrectPick(round);
  const base = (correctTop ? perPick : 0) + (correctBottom ? perPick : 0);
  const perfectRead = correctTop && correctBottom;
  const points = perfectRead ? Math.round(base * PERFECT_READ_MULTIPLIER) : base;

  return { correctTop, correctBottom, perfectRead, points };
}

/* -------------------------------------------------------------------------- */
/* One turn, whole room                                                        */
/* -------------------------------------------------------------------------- */

export interface ScoreTurnInput {
  round: number;
  /** Every player on the roster, including disconnected and removed ones. */
  playerIds: readonly PlayerId[];
  /** Scores nothing on their own turn — they author the canonical answer. */
  turnPlayerId: PlayerId;
  /** The canonical ranking, first place first. */
  ranking: Ranking;
  /** Only predictors who submitted both picks before the lock appear here. */
  predictions: Readonly<Record<PlayerId, Prediction>>;
  /** Host-skipped turns are voided: nobody scores (SPEC §5.4). */
  voided?: boolean;
}

/**
 * Deltas for every player on the roster, in roster order. Players who did not
 * submit, and the turn player, get an explicit zero row rather than being
 * omitted — the reveal screen shows a delta for everyone.
 */
export function scoreTurn(input: ScoreTurnInput): ScoreDelta[] {
  const { round, playerIds, turnPlayerId, ranking, predictions, voided = false } = input;

  return playerIds.map((playerId): ScoreDelta => {
    if (voided || playerId === turnPlayerId) {
      return { playerId, ...NOTHING };
    }
    const score = scorePrediction(round, predictions[playerId] ?? null, ranking);
    return { playerId, ...score };
  });
}

/** Convenience: deltas as a plain points map, for accumulating totals. */
export function pointsByPlayer(deltas: readonly ScoreDelta[]): Record<PlayerId, number> {
  const out: Record<PlayerId, number> = {};
  for (const d of deltas) out[d.playerId] = d.points;
  return out;
}

/* -------------------------------------------------------------------------- */
/* Attribution (SPEC §2.3 phase 4)                                             */
/* -------------------------------------------------------------------------- */

/**
 * Who predicted each ranked player for top and for bottom — correct and
 * incorrect alike. Returned in ranking order.
 */
export function buildAttribution(
  ranking: Ranking,
  predictions: Readonly<Record<PlayerId, Prediction>>,
): Attribution[] {
  const entries = Object.entries(predictions) as [PlayerId, Prediction][];
  return ranking.map((playerId, rank) => ({
    playerId,
    rank,
    predictedTopBy: entries.filter(([, p]) => p.top === playerId).map(([id]) => id),
    predictedBottomBy: entries.filter(([, p]) => p.bottom === playerId).map(([id]) => id),
  }));
}

/* -------------------------------------------------------------------------- */
/* Leaderboards (SPEC §2.4 — end of round only)                                */
/* -------------------------------------------------------------------------- */

export interface ComputeLeaderboardInput {
  round: number;
  final: boolean;
  playerIds: readonly PlayerId[];
  /** Every completed turn so far. */
  turns: readonly TurnRecord[];
}

/** Totals across all turns, plus the points earned in `round`. Ties share a rank. */
export function computeLeaderboard(input: ComputeLeaderboardInput): Leaderboard {
  const totals = new Map<PlayerId, number>();
  const roundPoints = new Map<PlayerId, number>();
  for (const id of input.playerIds) {
    totals.set(id, 0);
    roundPoints.set(id, 0);
  }

  for (const turn of input.turns) {
    for (const [id, points] of Object.entries(turn.points) as [PlayerId, number][]) {
      if (!totals.has(id)) continue;
      totals.set(id, (totals.get(id) ?? 0) + points);
      if (turn.round === input.round) {
        roundPoints.set(id, (roundPoints.get(id) ?? 0) + points);
      }
    }
  }

  const sorted = [...input.playerIds].sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));

  const rows: LeaderboardRow[] = [];
  let previousTotal: number | null = null;
  let previousRank = 0;
  sorted.forEach((playerId, index) => {
    const total = totals.get(playerId) ?? 0;
    const rank = previousTotal !== null && total === previousTotal ? previousRank : index + 1;
    previousTotal = total;
    previousRank = rank;
    rows.push({ playerId, total, roundPoints: roundPoints.get(playerId) ?? 0, rank });
  });

  return { round: input.round, final: input.final, rows };
}

/* -------------------------------------------------------------------------- */
/* Superlatives (SPEC §2.6)                                                    */
/* -------------------------------------------------------------------------- */

export interface ComputeSuperlativesInput {
  playerIds: readonly PlayerId[];
  turns: readonly TurnRecord[];
  /** Minimum predictions naming a player before "unreadable" can be awarded. */
  minSampleSize?: number;
}

interface Tally {
  /** Times this player was predicted for bottom by anyone. */
  pickedForBottom: number;
  /** Picks this player made, and how many were right. */
  picksMade: number;
  picksCorrect: number;
  /** Picks naming this player, and how many of those were wrong. */
  namedIn: number;
  namedWrong: number;
}

function emptyTally(): Tally {
  return { pickedForBottom: 0, picksMade: 0, picksCorrect: 0, namedIn: 0, namedWrong: 0 };
}

/**
 * All four superlatives, computed from data the room already holds. Ties award
 * jointly. A superlative with no eligible data returns an empty `playerIds`,
 * which the client should render as "not awarded" rather than crash on.
 */
export function computeSuperlatives(input: ComputeSuperlativesInput): Superlative[] {
  const { playerIds, turns, minSampleSize = 2 } = input;
  const scored = turns.filter((t) => !t.voided);

  const tallies = new Map<PlayerId, Tally>();
  for (const id of playerIds) tallies.set(id, emptyTally());

  /** turnIndex -> fraction of picks in that turn that were wrong. */
  const contrarian: { turnIndex: number; turnPlayerId: PlayerId; wrongRate: number; picks: number }[] = [];

  for (const turn of scored) {
    const { top, bottom } = rankingEnds(turn.ranking);
    let picks = 0;
    let wrong = 0;

    for (const [predictorId, prediction] of Object.entries(turn.predictions) as [
      PlayerId,
      Prediction,
    ][]) {
      const predictor = tallies.get(predictorId);
      const topRight = top !== null && prediction.top === top;
      const bottomRight = bottom !== null && prediction.bottom === bottom;

      picks += 2;
      wrong += (topRight ? 0 : 1) + (bottomRight ? 0 : 1);

      if (predictor) {
        predictor.picksMade += 2;
        predictor.picksCorrect += (topRight ? 1 : 0) + (bottomRight ? 1 : 0);
      }

      const namedTop = tallies.get(prediction.top);
      if (namedTop) {
        namedTop.namedIn += 1;
        if (!topRight) namedTop.namedWrong += 1;
      }

      const namedBottom = tallies.get(prediction.bottom);
      if (namedBottom) {
        namedBottom.namedIn += 1;
        namedBottom.pickedForBottom += 1;
        if (!bottomRight) namedBottom.namedWrong += 1;
      }
    }

    if (picks > 0) {
      contrarian.push({
        turnIndex: turn.index,
        turnPlayerId: turn.turnPlayerId,
        wrongRate: wrong / picks,
        picks,
      });
    }
  }

  const ordered = [...playerIds];

  const winnersBy = (
    value: (t: Tally) => number | null,
  ): { ids: PlayerId[]; value: number } => {
    let best = -Infinity;
    let ids: PlayerId[] = [];
    for (const id of ordered) {
      const tally = tallies.get(id);
      if (!tally) continue;
      const v = value(tally);
      if (v === null) continue;
      if (v > best) {
        best = v;
        ids = [id];
      } else if (v === best) {
        ids.push(id);
      }
    }
    if (!Number.isFinite(best) || best <= 0) return { ids: [], value: 0 };
    return { ids, value: best };
  };

  const mostBottom = winnersBy((t) => t.pickedForBottom);
  const bestPredictor = winnersBy((t) =>
    t.picksMade >= minSampleSize ? t.picksCorrect / t.picksMade : null,
  );
  const unreadable = winnersBy((t) =>
    t.namedIn >= minSampleSize ? t.namedWrong / t.namedIn : null,
  );

  let contrarianWinner: (typeof contrarian)[number] | null = null;
  for (const c of contrarian) {
    if (!contrarianWinner || c.wrongRate > contrarianWinner.wrongRate) contrarianWinner = c;
  }

  const pct = (n: number) => `${Math.round(n * 100)}%`;
  const bestPredictorTally = bestPredictor.ids[0] ? tallies.get(bestPredictor.ids[0]) : undefined;

  return [
    {
      kind: 'most-picked-for-bottom',
      title: 'Room favourite (for last place)',
      playerIds: mostBottom.ids,
      detail: mostBottom.ids.length
        ? `picked for bottom ${mostBottom.value} time${mostBottom.value === 1 ? '' : 's'}`
        : 'nobody stood out',
      value: mostBottom.value,
      turnIndex: null,
    },
    {
      kind: 'best-predictor',
      title: 'Best read on the room',
      playerIds: bestPredictor.ids,
      detail:
        bestPredictor.ids.length && bestPredictorTally
          ? `${bestPredictorTally.picksCorrect} of ${bestPredictorTally.picksMade} picks correct`
          : 'not enough picks',
      value: bestPredictor.value,
      turnIndex: null,
    },
    {
      kind: 'unreadable',
      title: 'Nobody could read them',
      playerIds: unreadable.ids,
      detail: unreadable.ids.length
        ? `${pct(unreadable.value)} of guesses about them were wrong`
        : 'not enough guesses',
      value: unreadable.value,
      turnIndex: null,
    },
    {
      kind: 'most-contrarian-ranking',
      title: 'Most disagreed-with ranking',
      playerIds: contrarianWinner ? [contrarianWinner.turnPlayerId] : [],
      detail: contrarianWinner
        ? `${pct(contrarianWinner.wrongRate)} of the room got that turn wrong`
        : 'no completed turns',
      value: contrarianWinner ? contrarianWinner.wrongRate : 0,
      turnIndex: contrarianWinner ? contrarianWinner.turnIndex : null,
    },
  ];
}

/* -------------------------------------------------------------------------- */
/* Misc helpers used by the DO                                                 */
/* -------------------------------------------------------------------------- */

/** Total turns in a game: one full pass per round (SPEC §2.2). */
export function totalTurns(playerCount: number, rounds: number): number {
  return playerCount * rounds;
}

/** ~2.5 min per turn (SPEC §2.2). */
export function estimateDurationMs(playerCount: number, rounds: number, msPerTurn: number): number {
  return totalTurns(playerCount, rounds) * msPerTurn;
}
